import { isNil } from 'lodash-es'
import { useMemo } from 'react'

import {
  AdditionalPriceOption,
  ConditionalCover,
  CoverLevelOption,
  CoverOption,
  getCoverLevelOption,
  Promotion,
  Quote,
} from '../domain'
import {
  getCoverLevelConfig,
  IncludedCoversConfig,
  ProductConfig,
} from '../state/configuration'
import { EMPTY_ARRAY } from '../util/array'
import { useCoverLevelOption } from './coverOption'

export const useIncludedCovers = (
  product: ProductConfig,
  coverLevelOption: CoverLevelOption | undefined,
): IncludedCoversConfig =>
  useMemo(
    () =>
      getCoverLevelConfig(product, coverLevelOption?.coverLevel)?.covers.included ?? [],
    [product, coverLevelOption],
  )

export const useConditionalCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
): readonly ConditionalCover[] => {
  const { coverLevelOption, coverLevelConfig } = useCoverLevelOption(quote, product)

  return useMemo(() => {
    const activeCovers: Set<string> = new Set(
      coverLevelOption?.basePremiumItems.flatMap((it) => it.covers) ?? [],
    )

    return (
      coverLevelConfig?.covers?.conditional?.map((coverCode) => ({
        coverCode,
        active: activeCovers.has(coverCode),
      })) ?? EMPTY_ARRAY
    )
  }, [quote, product])
}

export const useOptionalCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
): readonly CoverOption[] => {
  const { coverLevelOption, coverLevelConfig } = useCoverLevelOption(quote, product)

  return useMemo(() => {
    if (!coverLevelOption) return []

    return (
      coverLevelConfig?.covers?.optional?.map((cover) => {
        const coverOptionItem = coverLevelOption.coverOptionItems.find(
          (item) => item.coverOption === cover.code,
        )

        return {
          coverOption: cover.code,
          available: !isNil(coverOptionItem),
          active: coverOptionItem?.active,
          insurer: coverOptionItem?.insurer,
          premium: coverOptionItem?.premium ?? 0,
          totalPremium: coverOptionItem?.totalPremium ?? 0,
          taxAmount: coverOptionItem?.taxAmount ?? 0,
          contextualHelp: cover.contextualHelp,
        } satisfies CoverOption
      }) ?? EMPTY_ARRAY
    )
  }, [quote, product])
}

export const usePromotion = (quote: Quote | undefined): Promotion | undefined =>
  getCoverLevelOption(quote)?.promotion

export const useAdditionalPremiumBreakdown = (
  quote: Quote | undefined,
): readonly AdditionalPriceOption[] =>
  getCoverLevelOption(quote)?.additionalPremiumBreakdown ?? EMPTY_ARRAY
